import classNames from 'classnames';
import React, { useCallback, useRef } from 'react';

import { FocusRing, FocusScope } from '@react-aria/focus';
import {
  mergeProps,
  useEvent,
  useLayoutEffect,
  useResizeObserver,
  useValueEffect
} from '@react-aria/utils';

export const DatePickerInput: React.FC<any> = (props) => {
  const defaultRef = useRef();
  const {
    isDisabled,
    isQuiet,
    inputClassName,
    validationState,
    children,
    fieldProps,
    inputRef = defaultRef,
    className,
    autoFocus,
    style
  } = props;

  const [reservePadding, setReservePadding] = useValueEffect(false);
  const onResize = useCallback(
    () =>
      setReservePadding(function* (reservePadding) {
        if (reservePadding) {
          // Try to collapse padding if the content is clipped.
          if (inputRef.current.scrollWidth > inputRef.current.offsetWidth) {
            let width = inputRef.current!.parentElement.offsetWidth;
            yield false;

            // If removing padding causes a layout shift, add it back.
            if (inputRef.current!.parentElement.offsetWidth !== width) {
              yield true;
            }
          }
        } else {
          // Try to add padding if the content is not clipped.
          if (inputRef.current!.offsetWidth >= inputRef.current!.scrollWidth) {
            let width = inputRef.current.parentElement.offsetWidth;
            yield true;

            // If adding padding does not change the width (i.e. width is constrained), remove it again.
            if (inputRef.current.parentElement.offsetWidth === width) {
              yield false;
            }
          }
        }
      }),
    [inputRef, setReservePadding]
  );

  useLayoutEffect(onResize, [onResize]);
  useResizeObserver({
    ref: inputRef,
    onResize
  });

  useEvent(useRef(window), 'resize', onResize);

  const textfieldClass = React.useMemo(() => {
    return classNames('textfieldStyles', className, {
      'invalid-class bg-rose-100 border-rose-300': validationState === 'invalid',
      'valid-class': validationState === 'valid',
      'quiet-class': isQuiet
    });
  }, [className, isQuiet, validationState]);

  const inputClass = React.useMemo(() => {
    return classNames('inputStyles', inputClassName, {
      'is-disabled-class': isDisabled,
      'is-invalid-class': validationState === 'invalid',
      style: !!reservePadding
    });
  }, [inputClassName, reservePadding, isDisabled, validationState]);

  return (
    <div {...mergeProps(fieldProps)} className={textfieldClass} style={style}>
      <FocusRing
        focusClass={'is-focused-class'}
        focusRingClass={'focus-ring-class'}
        isTextInput
        within
      >
        <div role="presentation" className={inputClass}>
          <div
            ref={inputRef}
            className={'input-content-class flex focus:outline-none'}
            role="presentation"
          >
            <FocusScope autoFocus={autoFocus}>{children}</FocusScope>
          </div>
        </div>
      </FocusRing>
    </div>
  );
};
